import apiClient from './client';
import { CustomerDeposit } from '../types';

export const depositsApi = {
  /**
   * List all customer deposits for a client
   */
  listDeposits: async (clientId: string, options?: { accountId?: string | null }): Promise<CustomerDeposit[]> => {
    const params = new URLSearchParams();
    if (options?.accountId) {
      params.append('account_id', options.accountId);
    }
    const queryString = params.toString();
    const response = await apiClient.get<CustomerDeposit[]>(
      `/clients/${clientId}/deposits${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  },

  /**
   * Get a specific deposit
   */
  getDeposit: async (depositId: string): Promise<CustomerDeposit> => {
    const response = await apiClient.get<CustomerDeposit>(`/deposits/${depositId}`);
    return response.data;
  },

  /**
   * Update a deposit
   */
  updateDeposit: async (
    clientId: string,
    depositId: string,
    data: Partial<CustomerDeposit>
  ): Promise<CustomerDeposit> => {
    const response = await apiClient.put<CustomerDeposit>(
      `/clients/${clientId}/deposits/${depositId}`,
      data
    );
    return response.data;
  },

  /**
   * Approve/accept a deposit
   */
  approveDeposit: async (clientId: string, depositId: string): Promise<CustomerDeposit> => {
    const response = await apiClient.post<CustomerDeposit>(
      `/clients/${clientId}/deposits/${depositId}/accept`,
      {}
    );
    return response.data;
  },

  /**
   * Reject a deposit
   */
  rejectDeposit: async (clientId: string, depositId: string): Promise<CustomerDeposit> => {
    const response = await apiClient.post<CustomerDeposit>(
      `/clients/${clientId}/deposits/${depositId}/reject`,
      {}
    );
    return response.data;
  },

  /**
   * Unlock a deposit (reset to pending for re-editing)
   */
  unlockDeposit: async (clientId: string, depositId: string): Promise<CustomerDeposit> => {
    const response = await apiClient.post<CustomerDeposit>(
      `/clients/${clientId}/deposits/${depositId}/unlock`,
      {}
    );
    return response.data;
  },

  /**
   * Assign a deposit to a customer
   */
  assignToCustomer: async (
    clientId: string,
    depositId: string,
    customerId: string
  ): Promise<CustomerDeposit> => {
    const response = await apiClient.post<CustomerDeposit>(
      `/clients/${clientId}/deposits/${depositId}/assign-customer`,
      { customer_id: customerId }
    );
    return response.data;
  },

  /**
   * Upload customer deposits from a file
   */
  uploadDeposits: async (clientId: string, formData: FormData): Promise<void> => {
    await apiClient.post(`/clients/${clientId}/deposits/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Delete a deposit
   */
  deleteDeposit: async (depositId: string): Promise<void> => {
    await apiClient.delete(`/deposits/${depositId}`);
  },

  /**
   * Bulk delete duplicate deposits
   * Only deposits marked as duplicates can be deleted
   */
  bulkDeleteDeposits: async (
    clientId: string,
    depositIds: string[]
  ): Promise<{ deleted_count: number }> => {
    const response = await apiClient.delete<{ deleted_count: number }>(
      `/clients/${clientId}/deposits/bulk`,
      { data: { deposit_ids: depositIds } }
    );
    return response.data;
  },
};
